// src/Components/PerformanceGroupHeatmap/index.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";

// Performance group color mapping
export const GROUP_COLORS = {
  Star: "#0AB39C",
  "Cash Cow": "#F7B84B",
  Puzzle: "#F3F6F9",
  Dog: "#F06548",
  "Not Sold": "#383E42",
};

const PerformanceGroupHeatmap = ({
  data = [],
  showLegend = true,
}) => {
  const containerRef = useRef(null);
  const [cellSize, setCellSize] = useState(20);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);

  // Format date: 28Jun -> 28-Jun
  const formatDate = (dateStr) => {
    if (!dateStr) return "";

    if (/^\d{2}[A-Za-z]{3}$/.test(dateStr)) {
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 5);
      return `${day}-${month}`;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }

    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${day}-${month}`;
  };

  // Full date for tooltip
  const formatFullDate = (dateStr) => {
    if (!dateStr) return "";

    if (/^\d{2}[A-Za-z]{3}$/.test(dateStr)) {
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 5);
      return `${day}-${month}`;
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return dateStr;
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Calculate cell size based on available width
  const calculateCellSize = useCallback(() => {
    if (!containerRef.current || !data || data.length === 0) {
      return 20;
    }

    // Get the actual container width
    const containerWidth = containerRef.current.clientWidth - 16;
    const dataLength = data.length;
    
    // Calculate gaps
    const gapSize = 2;
    const totalGaps = (dataLength - 1) * gapSize;
    
    // Calculate ideal cell size
    let idealSize = (containerWidth - totalGaps) / dataLength;
    
    // Set min and max sizes
    const minSize = 10;
    const maxSize = 25;
    
    // For small screens, allow smaller cells
    if (window.innerWidth < 768) {
      return Math.min(Math.max(idealSize, 8), maxSize);
    }
    
    return Math.min(Math.max(idealSize, minSize), maxSize);
  }, [data]);

  // Handle resize and data changes
  useEffect(() => {
    const updateSize = () => {
      const newSize = calculateCellSize();
      setCellSize(newSize);
      setIsSmallScreen(window.innerWidth < 768);
    };

    updateSize();

    // Use ResizeObserver for better responsiveness
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateSize();
      });
      resizeObserver.observe(containerRef.current);
      
      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, [calculateCellSize]);

  // Recalculate when data changes
  useEffect(() => {
    const newSize = calculateCellSize();
    setCellSize(newSize);
  }, [calculateCellSize]);

  // Font size based on cell size
  const getFontSize = () => {
    if (isSmallScreen) return "6px";
    if (cellSize > 20) return "9px";
    if (cellSize > 15) return "8px";
    return "7px";
  };

  // Cell height based on screen size
  const getCellHeight = () => {
    return `${cellSize}px`;
  };

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "#6b7280",
          background: "#f9fafb",
          borderRadius: "6px",
        }}
      >
        No performance data available
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        background: "white",
        borderRadius: "6px",
        padding: "20px 4px 8px 4px",
        width: "100%",
        maxWidth: "100%",
          overflow: "visible",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflow: "visible",
        }}
      >
        {/* DATE HEADER */}
        <div
          style={{
            display: "flex",
            gap: "2px",
            paddingLeft: "10px",
            flexWrap: "nowrap",
            height: isSmallScreen ? "28px" : "38px",
            marginBottom: "6px",
            overflow: "visible",
          }}
        >
          {data.map((item, index) => {
            const date = item.row_dt || "";
            const fontSize = getFontSize();
            
            return (
              <div
                key={`header-${index}`}
                style={{
                  flex: "0 0 auto",
                  width: `${cellSize}px`,
                  textAlign: "center",
                  fontSize: fontSize,
                  color: "#1a1a1a",
                  fontWeight: "600",
                  lineHeight: "1",
                  whiteSpace: "nowrap",
                  transform: "rotate(-45deg)",
                  transformOrigin: "bottom center",
                  marginTop: "26px",
                }}
              >
                {formatDate(date)}
              </div>
            );
          })}
        </div>

        {/* PERFORMANCE GROUP CELLS */}
        <div
          style={{
            display: "flex",
            gap: "2px",
            padding: "2px 0",
            flexWrap: "nowrap",
            overflow: "visible",
          }}
        >
          {data.map((item, index) => {
            const group = item.performance_group || "Not Sold";
            const color = GROUP_COLORS[group] || GROUP_COLORS["Not Sold"];
            const date = item.row_dt || "";

            return (
              <div
                key={`cell-${index}`}
                style={{
                  flex: "0 0 auto",
                  width: `${cellSize}px`,
                  position: "relative",
                }}
                onMouseEnter={() => setHoveredCell(index)}
                onMouseLeave={() => setHoveredCell(null)}
              >
                <div
                  style={{
                    width: "100%",
                    height: getCellHeight(),
                    backgroundColor: color,
                    borderRadius: "2px",
                    cursor: "default",
                    border: "1px solid rgba(255,255,255,0.3)",
                    boxSizing: "border-box",
                  }}
                />
                {hoveredCell === index && (
                  <div
                    style={{
                      position: "absolute",
                      zIndex: 10,
                      top: "calc(100% + 6px)",
                      left: "50%",
                      transform: "translateX(-50%)",
                      minWidth: "170px",
                      padding: "11px 13px",
                      backgroundColor: "white",
                      color: "#1f2937",
                      border: "1px solid #9ca3af",
                      borderRadius: "6px",
                      boxShadow: "0 5px 16px rgba(0, 0, 0, 0.2)",
                      fontSize: "14px",
                      lineHeight: "1.5",
                      fontWeight: "500",
                      textAlign: "left",
                      pointerEvents: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <div style={{ fontWeight: "700", color: "#111827", marginBottom: "3px" }}>
                      {formatFullDate(date)}
                    </div>
                    <div>
                      <span style={{ color: "#6b7280" }}>Group: </span>
                      {group}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* LEGEND */}
      {showLegend && (
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginTop: "8px",
            paddingTop: "6px",
            borderTop: "1px solid #f0f0f0",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {Object.entries(GROUP_COLORS).map(([group, color]) => (
            <div
              key={group}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                fontSize: "11px",
                color: "#374151",
              }}
            >
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "2px",
                  backgroundColor: color,
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              />
              {group}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

PerformanceGroupHeatmap.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      row_dt: PropTypes.string,
      performance_group: PropTypes.string,
    })
  ),
  showLegend: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PerformanceGroupHeatmap.defaultProps = {
  data: [],
  showLegend: true,
  height: 30, // Default height for cells
};

export default PerformanceGroupHeatmap;